"use client";

import { useCallback, useEffect, useState } from "react";
import {
  CUSTOM_PARKING_EVENT,
  CUSTOM_PARKING_STORAGE_KEY,
  loadCustomParkings,
  removeCustomParking,
  saveCustomParking,
  type CustomParkingRecord,
  type StoredCustomParkingRecord,
} from "../lib/customParkingStorage";

const isBrowser = typeof window !== "undefined";

export function useCustomParkings() {
  const [records, setRecords] = useState<StoredCustomParkingRecord[]>(() => (isBrowser ? loadCustomParkings() : []));

  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    const sync = () => {
      setRecords(loadCustomParkings());
    };

    sync();

    const handleCustomEvent = () => {
      sync();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== CUSTOM_PARKING_STORAGE_KEY) {
        return;
      }
      sync();
    };

    window.addEventListener(CUSTOM_PARKING_EVENT, handleCustomEvent as EventListener);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(CUSTOM_PARKING_EVENT, handleCustomEvent as EventListener);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const upsert = useCallback((record: CustomParkingRecord) => {
    if (!isBrowser) {
      return;
    }
    const next = saveCustomParking(record);
    setRecords(next);
  }, []);

  const remove = useCallback((id: string) => {
    if (!isBrowser) {
      return;
    }
    const next = removeCustomParking(id);
    setRecords(next);
  }, []);

  return {
    records,
    upsert,
    remove,
  };
}
